import React, { useState } from "react";
import { Box, Typography, InputBase, Chip, CircularProgress, makeStyles } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import { dtcBadge } from "./severity";
import { useVehicleCrash } from "./useVehicleCrash";
import { Vehicle } from "./types";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#f9fafb" },
  header: { backgroundColor: "#fff", borderBottom: "1px solid #e5e7eb", padding: "16px 24px", flexShrink: 0 },
  titleBlock: { marginBottom: 12 },
  title: { fontWeight: 600, color: "#111827" },
  subtitle: { fontSize: 12, color: "#6b7280", marginTop: 2 },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
    maxWidth: 360,
  },
  searchIcon: { fontSize: 16, color: "#9ca3af" },
  searchInput: { fontSize: 13, flex: 1 },
  list: { flex: 1, overflowY: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 16 },
  loadingRow: { display: "flex", justifyContent: "center", padding: "32px 0" },
  emptyMessage: { fontSize: 12, color: "#9ca3af", textAlign: "center", marginTop: 16 },
  card: { backgroundColor: "#fff", borderRadius: 12, border: "1px solid #f3f4f6", boxShadow: theme.shadows[1], overflow: "hidden" },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderBottom: "1px solid #f3f4f6",
  },
  cardHeaderLeft: { display: "flex", alignItems: "center", gap: 12 },
  cardIcon: { fontSize: 16, color: "#9ca3af" },
  plate: { fontWeight: 600, fontSize: 13, color: "#111827" },
  makeModel: { fontSize: 12, color: "#9ca3af", marginLeft: 8 },
  countChip: { backgroundColor: dtcBadge.bg, color: dtcBadge.color, border: `1px solid ${dtcBadge.border}`, fontWeight: 500 },
  crashRow: { display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 16px" },
  crashRowBordered: { borderTop: "1px solid #f9fafb" },
  dot: { width: 8, height: 8, borderRadius: "50%", backgroundColor: "#ef4444", flexShrink: 0, marginTop: 6 },
  crashBody: { flex: 1, minWidth: 0 },
  badgeRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  severityChip: { height: 18, fontSize: 10, backgroundColor: "#fee2e2", color: "#b91c1c" },
  directionChip: { height: 18, fontSize: 10, backgroundColor: "#f3f4f6", color: "#6b7280" },
  address: { fontSize: 12, color: "#4b5563", marginTop: 4 },
  datetime: { fontSize: 10, color: "#9ca3af", marginTop: 4 },
}));

interface CrashPageProps {
  vehicles: Vehicle[];
  loading: boolean;
}

export function CrashPage({ vehicles, loading }: CrashPageProps) {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const trackedVehicles = vehicles.filter((v) => !!v.telemetryDeviceId);
  const { crashesByEsn, loading: crashLoading } = useVehicleCrash(trackedVehicles.map((v) => v.telemetryDeviceId));

  // Only vehicles that actually have crash events in the window are shown — no "No crashes" filler rows.
  const withCrashes = trackedVehicles.filter((v) => (crashesByEsn[v.telemetryDeviceId]?.length ?? 0) > 0);
  const filtered = withCrashes.filter(
    (v) => v.plate.toLowerCase().includes(search.toLowerCase()) || v.make.toLowerCase().includes(search.toLowerCase())
  );
  const totalCrashes = trackedVehicles.reduce((n, v) => n + (crashesByEsn[v.telemetryDeviceId]?.length ?? 0), 0);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.titleBlock}>
          <Typography className={classes.title}>Crash Events</Typography>
          <Typography className={classes.subtitle}>
            {totalCrashes} crash event{totalCrashes === 1 ? "" : "s"} in the last 24 hours · vehicles without a telemetry device aren't shown
          </Typography>
        </Box>
        <Box className={classes.searchBox}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase placeholder="Search vehicle…" value={search} onChange={(e) => setSearch(e.target.value)} className={classes.searchInput} />
        </Box>
      </Box>

      <Box className={classes.list}>
        {(loading || crashLoading) && (
          <Box className={classes.loadingRow}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && !crashLoading && filtered.length === 0 && (
          <Typography className={classes.emptyMessage}>
            {trackedVehicles.length === 0 ? "No vehicles with a telemetry device found." : "No crash events found."}
          </Typography>
        )}

        {filtered.map((v) => {
          const crashes = crashesByEsn[v.telemetryDeviceId] ?? [];
          return (
            <Box key={v.id} className={classes.card}>
              <Box className={classes.cardHeader}>
                <Box className={classes.cardHeaderLeft}>
                  <ReportProblemIcon className={classes.cardIcon} />
                  <Box>
                    <Typography component="span" className={classes.plate}>{v.plate}</Typography>
                    <Typography component="span" className={classes.makeModel}>{v.make} {v.model}</Typography>
                  </Box>
                </Box>
                <Chip label={`${crashes.length} event${crashes.length > 1 ? "s" : ""}`} size="small" className={classes.countChip} />
              </Box>
              {crashes.map((c, i) => {
                const address = [c.addressLine1, c.addressLine2, c.city, c.state, c.country].filter(Boolean).join(", ");
                return (
                  <Box key={`${c.crashId}-${i}`} className={`${classes.crashRow} ${i > 0 ? classes.crashRowBordered : ""}`}>
                    <Box className={classes.dot} />
                    <Box className={classes.crashBody}>
                      <Box className={classes.badgeRow}>
                        {c.severity && <Chip label={c.severity} size="small" className={classes.severityChip} />}
                        {c.crashDirection && <Chip label={c.crashDirection} size="small" className={classes.directionChip} />}
                      </Box>
                      {address && <Typography className={classes.address}>{address}</Typography>}
                      <Typography className={classes.datetime}>{c.datetime}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
